import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaPhoneAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import NavbarMenu from "../heroSection/NavbarMenu";
import { contactAPI } from "../../api/apiSystem";
import "./ContactPage.css";
import { useTranslation } from "react-i18next";
import Footer from "../footer/Footer";
import SEO from "../SEO";
import { settingsAPI, socialsAPI } from "../../api/apiSystem";
import AppDownloadSection from "../heroSection/AppDownloadSection";

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const [settingsRes] = await Promise.all([
          settingsAPI.index(),
          socialsAPI.index(),
        ]);

        if (settingsRes?.success && settingsRes.data?.data?.length > 0) {
          setSettings(settingsRes.data.data[0]);
        }

      } catch (err) {
        console.error("Error fetching settings:", err);
        setError("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!form.name || !form.email || !form.message) {
      Swal.fire({
        icon: "warning",
        title: t("contact.alerts.missingInfo"),
        text: t("contact.alerts.fillRequired"),
        confirmButtonColor: "#ff7b00",
      });
      return;
    }

    setLoading(true);

    try {
      const res = await contactAPI.store(form);

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("contact.successMessage"),
          confirmButtonColor: "#ff7b00",
          timer: 3000,
          showConfirmButton: true,
        });
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        Swal.fire({
          icon: "error",
          title: t("contact.alerts.submissionFailed"),
          text: t("contact.errorMessage") + (res.message || t("contact.alerts.unknownError")),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      Swal.fire({
        icon: "error",
        title: t("contact.alerts.connectionError"),
        text: t("contact.alerts.sendFailed"),
        confirmButtonColor: "#ff7b00",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="TransCirox – Kontakt | Schreiben Sie uns"
        description="Haben Sie Fragen zu Ihrem Transport oder Umzug? Kontaktieren Sie das TransCirox-Team – wir helfen Ihnen gerne weiter."
        keywords="Kontakt, TransCirox Kontakt, Transport Anfrage, Umzug Anfrage, Kundenservice"
      />
      <NavbarMenu />
      <div className="contact-section py-5">
        <Container>
          <Row className="justify-content-center align-items-center">
            <Col md={6}>
              <Card className="p-4 shadow border-0">
                <Card.Body>
                  <h3 className="mb-4 text-dark fw-bold text-center">
                    {t("contact.title")}
                  </h3>
                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group controlId="formName">
                          <Form.Label>{t("contact.nameLabel")}</Form.Label>
                          <div className="position-relative">
                            <FaUser className="form-icon" />
                            <Form.Control
                              type="text"
                              name="name"
                              placeholder={t("contact.namePlaceholder")}
                              className="ps-5"
                              value={form.name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formEmail">
                          <Form.Label>{t("contact.emailLabel")}</Form.Label>
                          <div className="position-relative">
                            <FaEnvelope className="form-icon" />
                            <Form.Control
                              type="email"
                              name="email"
                              placeholder={t("contact.emailPlaceholder")}
                              className="ps-5"
                              value={form.email}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formPhone">
                          <Form.Label>{t("contact.phoneLabel")}</Form.Label>
                          <div className="position-relative">
                            <FaPhone className="form-icon" />
                            <Form.Control
                              type="text"
                              name="phone"
                              placeholder={t("contact.phonePlaceholder")}
                              className="ps-5"
                              value={form.phone}
                              onChange={handleChange}
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group controlId="formMessage">
                          <Form.Label>{t("contact.messageLabel")}</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="message"
                            rows={4}
                            placeholder={t("contact.messagePlaceholder")}
                            value={form.message}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Button
                          type="submit"
                          variant="warning"
                          className="w-100 bg-orange-500 text-white mt-3"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              {t("contact.sendingButton")}
                            </>
                          ) : (
                            t("contact.submitButton")
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} className="mt-4 mt-md-0">
              {/* الخريطة */}
              <div className="map-responsive rounded shadow overflow-hidden mb-4">
                <iframe
                  title="Google Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2413.102617933352!2d7.465124723529055!3d50.99404607170109!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bec917f0efd6e1%3A0x3c2adac5dc7e7836!2zSGF1cHRzdHJhw59lIDEwLCA1MTc2NiBFbmdlbHNraXJjaGVuLVLDvG5kZXJvdGgsINij2YTZhdin2YbZitin!5e1!3m2!1sar!2seg!4v1783276976327!5m2!1sar!2seg"
                  width="100%"
                  height="300"
                  frameBorder="0"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>

              {/* معلومات الاتصال */}
              <div className="contact-info-wrapper">
                <h5 className="contact-title mb-3">
                  {settings?.footer_col1_title || t("Footer.contact.title")}
                </h5>

                <Row className="contact-info-grid g-3">
                  {/* العنوان */}
                  <Col xs={12} md={6} lg={7}>
                    <div className="contact-item">
                      <div className="contact-icon-wrapper">
                        <FaMapMarkerAlt className="contact-icon" />
                      </div>
                      <div className="contact-details">
                        <span className="contact-label">{t("Footer.address", "Adresse")}</span>
                        <span 
                          className="contact-value"
                          dangerouslySetInnerHTML={{ __html: settings?.footer_address || t("Footer.addressPlaceholder", "Musterstraße 1, 12345 Berlin") }}
                        />
                      </div>
                    </div>
                  </Col>

                  {/* الهاتف */}
                  <Col xs={12} md={6} lg={6}>
                    <div className="contact-item">
                      <div className="contact-icon-wrapper">
                        <FaPhoneAlt className="contact-icon" />
                      </div>
                      <div className="contact-details">
                        <span className="contact-label">{t("Footer.phone", "Telefon")}</span>
                        <span 
                          className="contact-value"
                          dangerouslySetInnerHTML={{ __html: settings?.footer_phone || t("Footer.phonePlaceholder", "+49 123 456 789") }}
                        />
                      </div>
                    </div>
                  </Col>

                  {/* البريد الإلكتروني */}
                  <Col xs={12} md={6} lg={6}>
                    <div className="contact-item">
                      <div className="contact-icon-wrapper">
                        <FaEnvelope className="contact-icon" />
                      </div>
                      <div className="contact-details">
                        <span className="contact-label">{t("Footer.email", "E-Mail")}</span>
                        <a 
                          href={`mailto:${settings?.footer_email || "info@example.com"}`}
                          className="contact-value contact-link"
                        >
                          {settings?.footer_email || "info@example.com"}
                        </a>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>

          </Row>
        </Container>
      </div>

      <AppDownloadSection />

      <Footer />
    </>
  );
}
